import React, { useState, useEffect, useRef } from 'react';
import AdminLayout from '../../components/AdminLayout';
import { api } from '../../lib/api';
import { useToast } from '../../components/Toast';

export default function Categories() {
  const [restaurant, setRestaurant] = useState(null);
  const [cats, setCats] = useState([]);
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', description: '' });
  const [saving, setSaving] = useState(false);
  const [savingOrder, setSavingOrder] = useState(false);
  const dragItem = useRef(null);
  const dragOver = useRef(null);
  const toast = useToast();

  useEffect(() => {
    api.get('/restaurants').then(data => {
      if (data.length > 0) { setRestaurant(data[0]); load(data[0].restaurant_id); }
    }).catch(() => {});
  }, []);

  const load = (rid) => api.get(`/restaurants/${rid}/categories`).then(setCats).catch(() => {});

  const openAdd = () => { setEditing(null); setForm({ name: '', description: '' }); setModal(true); };
  const openEdit = (c) => { setEditing(c); setForm({ name: c.name, description: c.description || '' }); setModal(true); };
  const close = () => { setModal(false); setEditing(null); };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/restaurants/${restaurant.restaurant_id}/categories/${editing.category_id}`, form);
        toast.success('Category updated!');
      } else {
        await api.post(`/restaurants/${restaurant.restaurant_id}/categories`, form);
        toast.success('Category created!');
      }
      load(restaurant.restaurant_id);
      close();
    } catch (err) { toast.error(err.message); } finally { setSaving(false); }
  };

  const handleDelete = async (cid) => {
    if (!window.confirm('Delete this category?')) return;
    try {
      await api.delete(`/restaurants/${restaurant.restaurant_id}/categories/${cid}`);
      toast.success('Deleted!');
      load(restaurant.restaurant_id);
    } catch (err) { toast.error(err.message); }
  };

  // ── Drag to reorder ───────────────────────────────────────────────
  const handleDragStart = (index) => { dragItem.current = index; };
  const handleDragEnter = (index) => { dragOver.current = index; };
  const handleDragEnd = async () => {
    const newCats = [...cats];
    const dragged = newCats.splice(dragItem.current, 1)[0];
    newCats.splice(dragOver.current, 0, dragged);
    dragItem.current = null;
    dragOver.current = null;
    setCats(newCats);
    // Save order to backend
    setSavingOrder(true);
    try {
      await api.put(`/restaurants/${restaurant.restaurant_id}/categories/reorder`, {
        order: newCats.map(c => c.category_id)
      });
      toast.success('Order saved!');
    } catch (err) {
      toast.error('Failed to save order');
    } finally {
      setSavingOrder(false);
    }
  };

  return (
    <AdminLayout title="Categories">
      <div className="flex justify-between items-center mb-6">
        <div style={{ fontSize: 13, color: '#888' }}>
          {savingOrder ? '⏳ Saving order...' : '⠿ Drag to reorder'}
        </div>
        <button className="btn btn-primary" onClick={openAdd}>+ Add Category</button>
      </div>

      {cats.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">📂</div>
          <h3>No categories yet</h3>
          <p>Add categories to organize your menu</p>
          <button className="btn btn-primary mt-4" onClick={openAdd}>+ Add First Category</button>
        </div>
      ) : (
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th style={{ width: 40 }}></th>
                <th>Name</th>
                <th>Description</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {cats.map((c, index) => (
                <tr
                  key={c.category_id}
                  draggable
                  onDragStart={() => handleDragStart(index)}
                  onDragEnter={() => handleDragEnter(index)}
                  onDragEnd={handleDragEnd}
                  onDragOver={e => e.preventDefault()}
                  style={{ cursor: 'grab', userSelect: 'none' }}
                >
                  <td style={{ color: '#ccc', fontSize: 18, textAlign: 'center' }}>⠿</td>
                  <td className="font-medium">{c.name}</td>
                  <td className="text-muted">{c.description || '—'}</td>
                  <td>
                    <div className="flex gap-2">
                      <button className="btn btn-outline btn-sm" onClick={() => openEdit(c)}>Edit</button>
                      <button className="btn btn-danger btn-sm" onClick={() => handleDelete(c.category_id)}>Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modal && (
        <div className="modal-overlay" onClick={close}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">{editing ? 'Edit Category' : 'Add Category'}</h2>
              <button className="modal-close" onClick={close}>✕</button>
            </div>
            <form onSubmit={handleSave}>
              <div className="modal-body space-y-4">
                <div className="form-group">
                  <label className="form-label">Name *</label>
                  <input className="form-input" value={form.name} onChange={e => setForm(p => ({...p, name: e.target.value}))} placeholder="e.g. Starters, Main Course..." required />
                </div>
                <div className="form-group">
                  <label className="form-label">Description</label>
                  <textarea className="form-textarea" value={form.description} onChange={e => setForm(p => ({...p, description: e.target.value}))} placeholder="Optional description..." />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-outline" onClick={close}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}